/* LAY "Make it a pair" (10-03): the cart drawer pair cards (snippets/lay-cart-pairs.liquid).
   One tap swaps one unit of the single for its duo bundle in a single /cart/update call, then hands the
   section HTML to the theme the same way the quantity buttons do (CartLinesUpdateEvent with a promise),
   so the drawer, the BAG count and the analytics listeners all update. Loaded once per page (module).
   F7 (10-17): the whole card is the button (Clarity showed dead taps on the image and the name). The tap
   shows "Added" at once; after the swap an "Undo" bar stays for a few seconds and swaps the bundle back to
   the original line (removing the duo by hand would take both scents out of the bag). One swap at a time. */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const UNDO_MS = 8000;
let inFlight = false;
/** @type {null | {fromVariant: string, fromTitle: string, fromQtyNow: number, toVariant: string, toKey: string, toQty: number, toTitle: string, timer: number, focus: boolean}} */
let undo = null;

const sectionIds = () => {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids).join(',');
};

let live = null;
const announce = (text) => {
  if (!live) {
    live = document.createElement('div');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('role', 'status');
    live.className = 'visually-hidden';
    document.body.appendChild(live);
  }
  live.textContent = '';
  setTimeout(() => {
    if (live) live.textContent = text;
  }, 60);
};

/* One cart update + one 'add' event (the event class maps 'add' lines by merchandiseId, every other action by
   cart line id, so a mixed swap cannot be described in one event). The resolved cart + sections carry the full
   new state, so the drawer and the BAG count are right. Marked as ours (__lay) so the Undo bar survives it. */
const swapLines = async (trigger, updates, eventVariant) => {
  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    const ev = new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: eventVariant, quantity: 1 }],
      promise: deferred.promise,
    });
    ev.__lay = true;
    trigger.dispatchEvent(ev);
  } catch (error) {
    deferred = null;
  }
  try {
    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({ updates, sections: sectionIds(), sections_url: window.location.pathname }),
      })
    );
    const data = await response.json();
    if (!response.ok || data.status || data.errors) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    if (!deferred) {
      window.location.reload();
      return null;
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: {
        sections: data.sections ?? undefined,
        items: data.items,
        itemCount: Number(data.item_count) || 0,
        source: 'lay-cart-pairs',
        didError: false,
      },
    });
    return data;
  } catch (error) {
    deferred?.reject(error);
    throw error;
  }
};

const qtyOf = (data, variantId) =>
  (data.items || []).reduce((n, item) => (String(item.variant_id) === String(variantId) ? n + (Number(item.quantity) || 0) : n), 0);

const dropBar = (fade) => {
  document.querySelectorAll('#cart-drawer .lay-pair-undo').forEach((bar) => {
    if (!fade) {
      bar.remove();
      return;
    }
    bar.classList.add('is-leaving');
    setTimeout(() => bar.remove(), 320);
  });
};

const clearUndo = (fade = false) => {
  if (undo) clearTimeout(undo.timer);
  undo = null;
  dropBar(fade);
};

/* The theme morphs the drawer after the promise resolves; place the bar after that (and again shortly after,
   in case a second morph replaced it). It sits right above the next offer (or at the end of the items). */
const placeBar = (u) => {
  if (undo !== u) return;
  const items = document.querySelector('#cart-drawer .cart-drawer__items');
  if (!items || items.querySelector('.lay-pair-undo')) return;
  const bar = document.createElement('div');
  bar.className = 'lay-pair-undo';
  const msg = document.createElement('span');
  msg.textContent = `${u.toTitle} added.`;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'lay-pair-undo__btn';
  btn.setAttribute('data-lay-pair-undo', '');
  btn.setAttribute('aria-label', `Undo, back to ${u.fromTitle}`);
  btn.textContent = 'Undo';
  bar.append(msg, btn);
  items.insertBefore(bar, items.querySelector('[data-lay-pairs]'));
  if (u.focus) btn.focus({ preventScroll: true });
};

const expire = (u) => {
  if (undo !== u) return;
  const btn = document.querySelector('#cart-drawer [data-lay-pair-undo]');
  if (btn && document.activeElement === btn && btn.matches(':focus-visible')) {
    btn.addEventListener('blur', () => expire(u), { once: true });
    return;
  }
  clearUndo(true);
};

const showUndo = (u) => {
  undo = u;
  requestAnimationFrame(() => requestAnimationFrame(() => placeBar(u)));
  setTimeout(() => placeBar(u), 250);
  setTimeout(() => placeBar(u), 800);
  u.timer = window.setTimeout(() => expire(u), UNDO_MS);
};

/* Any other cart change (quantity, remove, another add) ends the Undo window: its numbers would be stale. */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event && event.__lay) return;
  if (undo) clearUndo();
});

const showError = (anchor, text) => {
  const msg = document.createElement('p');
  msg.className = 'lay-pair__err';
  msg.setAttribute('role', 'alert');
  msg.textContent = text;
  anchor.insertAdjacentElement('afterend', msg);
};

const onSwap = async (card) => {
  const fromKey = card.dataset.fromKey || '';
  const fromQty = Math.max(1, parseInt(card.dataset.fromQty || '1', 10) || 1);
  const toId = card.dataset.to || '';
  if (!fromKey || !toId) return;

  const block = card.closest('[data-lay-pairs]');
  block?.querySelectorAll('.lay-pair__err').forEach((el) => el.remove());
  /* Keyboard users get the focus on Undo (the tapped card disappears in the re-render); a tap or a click
     focuses the button too in some browsers, so only a visible keyboard focus counts. */
  const hadFocus = document.activeElement === card && card.matches(':focus-visible');
  clearUndo();
  inFlight = true;
  card.setAttribute('aria-busy', 'true');
  card.classList.add('is-added');
  block?.classList.add('is-busy');

  /** @type {Record<string, number>} */
  const updates = {};
  updates[fromKey] = fromQty - 1;
  updates[toId] = 1;

  try {
    const data = await swapLines(card, updates, toId);
    if (!data) return;
    const toLine = (data.items || []).find((item) => String(item.variant_id) === String(toId));
    const toTitle = card.dataset.toTitle || 'Your bundle';
    announce(`${toTitle} added.`);
    showUndo({
      fromVariant: card.dataset.fromVariant || '',
      fromTitle: card.dataset.fromTitle || '',
      fromQtyNow: qtyOf(data, card.dataset.fromVariant || ''),
      toVariant: toId,
      toKey: toLine ? String(toLine.key) : '',
      toQty: toLine ? Number(toLine.quantity) || 1 : 1,
      toTitle,
      timer: 0,
      focus: hadFocus,
    });
  } catch (error) {
    card.removeAttribute('aria-busy');
    card.classList.remove('is-added');
    block?.classList.remove('is-busy');
    showError(card, 'Could not update your bag. Please try again.');
  } finally {
    inFlight = false;
  }
};

const onUndo = async (btn) => {
  const u = undo;
  if (!u || !u.fromVariant) return;
  clearTimeout(u.timer);
  inFlight = true;
  btn.setAttribute('aria-busy', 'true');

  /** @type {Record<string, number>} */
  const updates = {};
  updates[u.toKey || u.toVariant] = Math.max(0, u.toQty - 1);
  updates[u.fromVariant] = u.fromQtyNow + 1;

  try {
    await swapLines(btn, updates, u.fromVariant);
    clearUndo();
    announce(`Back to ${u.fromTitle}.`);
  } catch (error) {
    btn.removeAttribute('aria-busy');
    const bar = btn.closest('.lay-pair-undo');
    if (bar && !bar.nextElementSibling?.classList.contains('lay-pair__err')) {
      showError(bar, 'Could not undo. Please try again.');
    }
    u.timer = window.setTimeout(() => expire(u), UNDO_MS);
  } finally {
    inFlight = false;
  }
};

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const undoBtn = target.closest('[data-lay-pair-undo]');
  const card = undoBtn ? null : target.closest('[data-lay-pair-swap]');
  if (!undoBtn && !card) return;
  event.preventDefault();
  /* Double tap guard: nothing starts while a swap or an undo is running. */
  if (inFlight) return;
  if (undoBtn) {
    if (undoBtn.getAttribute('aria-busy') !== 'true') onUndo(undoBtn);
    return;
  }
  if (card.getAttribute('aria-busy') === 'true') return;
  onSwap(card);
});

/* ---- LAY "Still deciding?" (F7, 10-17; 10-18: no header opener, the button says "Save my bag") ---------------
   The block at the end of the drawer list (snippets/lay-cart-save.liquid). A reason chip shows the email field.
   The email goes to Klaviyo with the public key (client API): the Email List subscription, the bag
   link and the reason on the profile (the welcome email puts a "Back to your bag" block on top from these), and a
   "Saved Bag" event (the email for people already on the list). The bag link is a cart permalink, so it rebuilds
   the bag in any browser (the in-app browser's cart stays in the app). Hidden when Klaviyo already knows the
   visitor and after a save in this browser. The drawer morphs on every cart change: the state lives here and is
   put back on the new block. */
const SAVE = {
  site: 'Ut2qCJ',
  list: 'Wz8PsY',
  revision: '2026-07-15',
  source: 'Cart drawer: Still deciding',
  doneKey: 'lay_save_done',
};
const SAVE_REASONS = { scent: 'Not sure about the scent', price: 'Price', browsing: 'Just browsing', shipping: 'Shipping time' };
const SAVE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SAVE_ERR_EMAIL = "That email doesn't look right.";
const SAVE_ERR_FAIL = 'Something went wrong. Please try again.';
const SAVE_OK = 'Saved! Check your inbox in a few minutes.';
const saveState = { open: false, reason: '', email: '', err: '', busy: false };
const htmlRoot = document.documentElement;
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

try {
  if (localStorage.getItem(SAVE.doneKey)) htmlRoot.classList.add('lay-save-off');
} catch (error) {
  /* storage blocked: the block just shows */
}

let klaviyoChecked = false;
const checkKnown = () => {
  if (klaviyoChecked) return;
  const kl = window.klaviyo;
  if (!kl || typeof kl.isIdentified !== 'function') return;
  klaviyoChecked = true;
  try {
    Promise.resolve(kl.isIdentified())
      .then((known) => {
        if (known && !htmlRoot.classList.contains('lay-save-done')) htmlRoot.classList.add('lay-save-off');
      })
      .catch(() => {});
  } catch (error) {
    /* no answer: the block stays */
  }
};

const applySave = (block) => {
  if (!(block instanceof HTMLElement)) return;
  block.dataset.layInit = '1';
  block.classList.toggle('is-open', saveState.open);
  block.dataset.reason = saveState.reason;
  block.querySelectorAll('.lay-save__chip').forEach((chip) => {
    if (chip instanceof HTMLElement) chip.setAttribute('aria-pressed', String(chip.dataset.reason === saveState.reason));
  });
  const input = block.querySelector('.lay-save__input');
  if (input instanceof HTMLInputElement && document.activeElement !== input && input.value !== saveState.email) {
    input.value = saveState.email;
  }
  const err = block.querySelector('.lay-save__err');
  if (err instanceof HTMLElement) {
    err.textContent = saveState.err;
    err.hidden = !saveState.err;
  }
  const send = block.querySelector('.lay-save__send');
  if (send instanceof HTMLElement) {
    if (saveState.busy) send.setAttribute('aria-busy', 'true');
    else send.removeAttribute('aria-busy');
  }
};

const syncSave = () => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.querySelectorAll('[data-lay-save]').forEach((block) => {
    if (block instanceof HTMLElement && !block.dataset.layInit) {
      applySave(block);
      checkKnown();
    }
  });
};

const watchSave = () => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  /* childList: the morph swaps nodes; data-lay-init: the morph syncs our block to the server HTML in place */
  new MutationObserver(syncSave).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-lay-init'] });
  syncSave();
  checkKnown();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchSave);
else watchSave();

/* Bring the opened block into view inside the drawer list (after a reason chip): the whole block, the consent line
   included, if it fits, otherwise its top. The scroller is the list on mobile and the whole drawer content on desktop;
   the sticky summary covers the bottom of the scroller. */
const scrollerOf = (el) => {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return n;
  }
  return null;
};
const revealSave = (block) => {
  if (!(block instanceof HTMLElement)) return;
  const sc = scrollerOf(block);
  if (!sc) return;
  const summary = sc.querySelector('.cart-drawer__summary');
  const cover = summary && getComputedStyle(summary).position === 'sticky' ? summary.getBoundingClientRect().height : 0;
  const view = sc.getBoundingClientRect();
  const top = view.top;
  const bottom = view.bottom - cover;
  const r = block.getBoundingClientRect();
  let delta = 0;
  if (r.height > bottom - top - 16) delta = r.top - top - 8;
  else if (r.bottom > bottom - 8) delta = r.bottom - bottom + 8;
  else if (r.top < top + 8) delta = r.top - top - 8;
  if (Math.abs(delta) > 1) sc.scrollBy({ top: delta, behavior: reduceMotion() ? 'auto' : 'smooth' });
};

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !target.closest('#cart-drawer')) return;
  const chip = target.closest('.lay-save__chip');
  if (!(chip instanceof HTMLElement)) return;
  event.preventDefault();
  const block = chip.closest('[data-lay-save]');
  saveState.reason = chip.dataset.reason || '';
  saveState.open = true;
  saveState.err = '';
  applySave(block);
  requestAnimationFrame(() => revealSave(block));
});

document.addEventListener('input', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('lay-save__input')) return;
  saveState.email = input.value;
  if (saveState.err) {
    saveState.err = '';
    applySave(input.closest('[data-lay-save]'));
  }
});

const klaviyoPost = (path, data) =>
  fetch(`https://a.klaviyo.com/client/${path}/?company_id=${SAVE.site}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: SAVE.revision },
    body: JSON.stringify({ data }),
  });

/* The bag as it is now: a cart permalink (variant:quantity pairs; ?storefront=true opens the cart, not checkout). */
const currentBag = async () => {
  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const response = await fetch(`${rootPath}cart.js`, { headers: { Accept: 'application/json' } });
  const cart = await response.json();
  const items = (cart.items || []).filter((item) => Number(item.quantity) > 0);
  const path = items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
  return {
    url: path ? `${window.location.origin}/cart/${path}?storefront=true` : `${window.location.origin}/cart`,
    names: items.map((item) => (item.quantity > 1 ? `${item.product_title} x${item.quantity}` : item.product_title)).join(', '),
    lines: items.map((item) => ({ name: item.product_title, quantity: item.quantity, variant_id: item.variant_id })),
    count: Number(cart.item_count) || 0,
    value: (Number(cart.total_price) || 0) / 100,
  };
};

const identifyVisitor = (email) => {
  const kl = window.klaviyo;
  if (!kl) return;
  try {
    if (typeof kl.identify === 'function') kl.identify({ email });
    else if (typeof kl.push === 'function') kl.push(['identify', { email }]);
  } catch (error) {
    /* the browser link is a nice-to-have */
  }
};

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('lay-save__form')) return;
  event.preventDefault();
  if (saveState.busy) return;
  const block = form.closest('[data-lay-save]');
  const input = form.querySelector('.lay-save__input');
  const email = (input instanceof HTMLInputElement ? input.value : '').trim();
  saveState.email = email;
  if (!SAVE_EMAIL.test(email)) {
    saveState.err = SAVE_ERR_EMAIL;
    applySave(block);
    if (input instanceof HTMLInputElement) input.focus();
    return;
  }
  saveState.busy = true;
  saveState.err = '';
  applySave(block);
  const fail = (why) => {
    saveState.busy = false;
    saveState.err = SAVE_ERR_FAIL;
    applySave(document.querySelector('#cart-drawer [data-lay-save]'));
    console.warn('[lay-save]', why);
  };
  try {
    const bag = await currentBag();
    const reason = saveState.reason || 'none';
    const profile = { data: { type: 'profile', attributes: { email } } };
    const results = await Promise.allSettled([
      klaviyoPost('subscriptions', {
        type: 'subscription',
        attributes: { custom_source: SAVE.source, profile },
        relationships: { list: { data: { type: 'list', id: SAVE.list } } },
      }),
      klaviyoPost('profiles', {
        type: 'profile',
        attributes: {
          email,
          properties: {
            lay_bag_url: bag.url,
            lay_bag_items: bag.names,
            lay_bag_reason: reason,
            lay_bag_value: bag.value,
            lay_bag_saved_at: new Date().toISOString(),
          },
        },
      }),
      klaviyoPost('events', {
        type: 'event',
        attributes: {
          properties: {
            Reason: reason,
            ReasonLabel: SAVE_REASONS[reason] || '',
            BagURL: bag.url,
            ItemNames: bag.names,
            Items: bag.lines,
            ItemCount: bag.count,
            BagValue: bag.value,
            Source: 'Cart drawer',
          },
          metric: { data: { type: 'metric', attributes: { name: 'Saved Bag' } } },
          profile,
        },
      }),
    ]);
    const bad = results
      .map((result, i) => (result.status === 'fulfilled' && (result.value.ok || result.value.status === 202) ? '' : ['subscription', 'profile', 'event'][i]))
      .filter(Boolean);
    if (bad.length) {
      fail(`Klaviyo: ${bad.join(', ')}`);
      return;
    }
    saveState.busy = false;
    identifyVisitor(email);
    try {
      localStorage.setItem(SAVE.doneKey, String(Date.now()));
    } catch (error) {
      /* the block just shows again next time */
    }
    htmlRoot.classList.add('lay-save-done');
    applySave(document.querySelector('#cart-drawer [data-lay-save]'));
    announce(SAVE_OK);
  } catch (error) {
    fail(error);
  }
});
